import { memo, useMemo, useState } from 'react';
import {
  HorizontalGridLines,
  Hint,
  LineSeries,
  MarkSeries,
  XAxis,
  XYPlot,
  YAxis,
  makeWidthFlexible,
} from 'react-vis';

import styles from './styles.module.scss';
import {
  buildSeriesSegments,
  formatTrendingLegendLabel,
  formatTrendingTooltipLabel,
  parseTrendingLabelRange,
  hasYearChanged,
  formatCrossYearAxisLabel,
  type TTrendingLabelRange,
} from './utils';

import type { ITrendingChartCanvasProps } from './types';

const FlexibleXYPlot = makeWidthFlexible(XYPlot);

type TChartPoint = { x: number; y: number };
type THoveredValue = {
  id: string;
  label: string;
  color?: string;
  value: number;
};

const TrendingChartCanvas = memo(
  ({
    categories,
    series,
    maxValue,
    minValue,
    height,
    ariaLabel,
  }: ITrendingChartCanvasProps) => {
    const parsedLabelRanges = useMemo<TTrendingLabelRange[]>(
      () => categories.map((label) => parseTrendingLabelRange(label)),
      [categories],
    );

    const hasMultipleCategories = categories.length > 1;
    const isCrossYear =
      categories.length > 0 ? hasYearChanged(categories) : false;
    const xDomain: [number, number] = hasMultipleCategories
      ? [0, categories.length - 1]
      : [0, 1];

    const xTickValues = isCrossYear
      ? [Math.floor((categories.length - 1) / 2)]
      : categories.map((_, index) => index);
    const [hoveredPoint, setHoveredPoint] = useState<TChartPoint | null>(null);
    const adjustedMinValue = Math.max(0, minValue);
    const adjustedMaxValue = Math.max(4, maxValue);

    const crossYearAxisLabel = useMemo(() => {
      if (!isCrossYear) {
        return null;
      }

      return formatCrossYearAxisLabel(parsedLabelRanges);
    }, [isCrossYear, parsedLabelRanges]);

    const formatAxisLabel = (index: number) => {
      if (crossYearAxisLabel) {
        return crossYearAxisLabel;
      }

      const label = categories[index];
      if (!label) return '';

      const range = parsedLabelRanges[index];
      return formatTrendingLegendLabel(range, label);
    };

    const formatTooltipLabel = (index: number) => {
      const fallback = categories[index] ?? `Point ${index + 1}`;
      const range = parsedLabelRanges[index];
      return formatTrendingTooltipLabel(range, fallback);
    };

    const seriesSegments = useMemo(
      () =>
        series.map((item) => ({
          ...item,
          segments: buildSeriesSegments(item.values),
        })),
      [series],
    );

    const hoveredIndex = useMemo(
      () => (hoveredPoint ? Math.round(hoveredPoint.x) : null),
      [hoveredPoint],
    );

    const hoveredValues = useMemo<THoveredValue[]>(() => {
      if (hoveredIndex === null) return [];

      return series.reduce((acc, item) => {
        const value = item.values[hoveredIndex];
        if (typeof value === 'number' && Number.isFinite(value)) {
          acc.push({
            id: item.id,
            label: item.label,
            color: item.color,
            value,
          });
        }
        return acc;
      }, [] as THoveredValue[]);
    }, [hoveredIndex, series]);

    const hoveredLabel =
      hoveredIndex !== null &&
      hoveredIndex >= 0 &&
      hoveredIndex < categories.length
        ? formatTooltipLabel(hoveredIndex)
        : '';

    const hoveredMarkers = useMemo(() => {
      if (hoveredIndex === null) return [];

      return series.reduce((acc, item) => {
        const value = item.values[hoveredIndex];
        if (typeof value === 'number' && Number.isFinite(value)) {
          acc.push({
            id: item.id,
            color: item.color,
            data: [{ x: hoveredIndex, y: value }],
          });
        }
        return acc;
      }, [] as { id: string; color?: string; data: TChartPoint[] }[]);
    }, [hoveredIndex, series]);

    return (
      <div
        className={styles.chart}
        role="img"
        aria-label={ariaLabel}
        onMouseLeave={() => setHoveredPoint(null)}
      >
        <FlexibleXYPlot
          height={height}
          margin={{ top: 8, right: 16, bottom: 36, left: 44 }}
          yDomain={[adjustedMinValue, adjustedMaxValue]}
          xDomain={xDomain}
          className={styles['react-vis-plot']}
        >
          <HorizontalGridLines
            style={{ stroke: 'var(--ant-color-split)', opacity: 0.6 }}
          />

          <YAxis
            style={{
              line: { stroke: 'var(--ant-color-split)', strokeWidth: 0 },
              ticks: { stroke: 'transparent' },
              text: {
                fill: 'var(--ant-color-text-tertiary)',
                fontSize: 'var(--ant-font-size-sm)',
              },
            }}
          />

          <XAxis
            marginTop={20}
            tickValues={xTickValues}
            tickFormat={(value) => formatAxisLabel(value as number)}
            style={{
              line: { stroke: 'var(--ant-color-split)', strokeWidth: 0 },
              ticks: { stroke: 'transparent' },
              text: {
                fill: 'var(--ant-color-text-tertiary)',
                fontSize: 'var(--ant-font-size-sm)',
              },
            }}
          />

          {seriesSegments.map((item) =>
            item.segments.map((segment, index) => (
              <LineSeries
                key={`${item.id}-${index}`}
                data={segment}
                color={item.color}
                onNearestX={(datapoint) => {
                  if (
                    datapoint &&
                    Number.isFinite(datapoint.x) &&
                    Number.isFinite(datapoint.y)
                  ) {
                    setHoveredPoint({
                      x: datapoint.x,
                      y: datapoint.y,
                    });
                  }
                }}
              />
            )),
          )}

          {hoveredIndex !== null && hoveredValues.length > 0 && (
            <LineSeries
              data={[
                { x: hoveredIndex, y: adjustedMinValue },
                { x: hoveredIndex, y: adjustedMaxValue },
              ]}
              color="var(--ant-color-border)"
              style={{ strokeWidth: 1, pointerEvents: 'none' }}
            />
          )}

          {hoveredMarkers.map((item) => (
            <MarkSeries
              key={`${item.id}-hover-points`}
              data={item.data}
              color={item.color}
              style={{
                stroke: 'var(--ant-color-bg-container)',
                strokeWidth: 2,
              }}
            />
          ))}

          {hoveredPoint && hoveredValues.length > 0 && (
            <Hint value={hoveredPoint} className={styles.hint}>
              <div className={styles.tooltip}>
                <div className={styles['tooltip-date']}>{hoveredLabel}</div>
                <div className={styles['tooltip-list']}>
                  {hoveredValues.map((item) => (
                    <div key={item.id} className={styles['tooltip-row']}>
                      <span
                        className={styles['tooltip-swatch']}
                        style={{ backgroundColor: item.color }}
                      />
                      <span className={styles['tooltip-label']}>
                        {item.label}
                      </span>
                      <span className={styles['tooltip-value']}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Hint>
          )}
        </FlexibleXYPlot>
      </div>
    );
  },
);

TrendingChartCanvas.displayName = 'TrendingChartCanvas';

export default TrendingChartCanvas;
